import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  Avatar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Collapse,
} from "@mui/material";
import {
  AddCircleOutline,
  Print,
  MonitorWeight,
  LocalHospital,
  WarningAmber,
  Close,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "react-query";
import { QK } from "../../../../../base/qk";
import {
  getMedications,
  deleteMedication,
} from "../../../../../api/services/medication";
import { useSnackbar } from "notistack";
import { useUserProfile } from "../../../../../hooks/userProfile";
import useClientDetails from "../../../../../hooks/useClientDetails";
import { getGPDetails } from "../../../../../api/services/Client";
import { getAllergiesByID } from "../../../../../api/services/allergy";
import { getWeightByClientID } from "../../../../../api/services/observations";

import AddMedicationModal from "./AddMedicationModal";
import UpdateMedicationModal from "./UpdateMedicationModal";
import MedicationDetailsModal from "./MedicationDetailsModal";
import ConfirmModal from "../../../../ConfirmModal/ConfirmModal";
import {AdjustStockModal, RefillMedicationModal} from "./Dialog";
import MedicationsTable from "../MedsList/MedicationsTable";

const formatDateTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  const date = d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const time = d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${date} • ${time}`;
};

const InfoCard = ({ icon, iconBg, label, value, bg, extra }) => (
  <Box sx={{ flex: "1 1 30%", minWidth: "240px" }}>
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        borderRadius: "10px",
        border: "1px solid #e2e8f0",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        bgcolor: bg,
        height: "100%",
      }}
    >
      <Avatar sx={{ bgcolor: iconBg, width: 38, height: 38 }}>{icon}</Avatar>
      <Box>
        <Typography
          sx={{
            fontSize: "0.65rem",
            color: "#64748b",
            fontWeight: 600,
            letterSpacing: 0.3,
          }}
        >
          {label}
        </Typography>
        <Stack direction="row" alignItems="baseline" spacing={0.75}>
          <Typography
            sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#0f172a" }}
          >
            {value}
          </Typography>
          {extra}
        </Stack>
      </Box>
    </Paper>
  </Box>
);

const MedsList = () => {
  const [showAllergies, setShowAllergies] = useState(false);
  const [showMedicationModal, setShowMedicationModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showMedicationDetails, setShowMedicationDetails] = useState(false);
  const [showUpdateMedicationModal, setShowUpdateMedicationModal] =
    useState(false);
  const [showRefillModal, setShowRefillModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [medId, setMedId] = useState(null);
  const [selectedMedObject, setSelectedMedObject] = useState(null);
  const [selectedAdjustMed, setSelectedAdjustMed] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const { id: clientID } = useParams();
  const profile = useUserProfile();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { data: clientMedications, isLoading } = useQuery(
    [QK.clientMedications, clientID, currentPage],
    () => getMedications(clientID, currentPage),
    { enabled: !!clientID }
  );

  const { data: allergies } = useQuery(
    [QK.allergies, clientID],
    () => getAllergiesByID(clientID),
    { enabled: !!clientID }
  );

  const { data: weights } = useQuery([QK.weights, clientID], () =>
    getWeightByClientID(clientID)
  );

  const { data: gpDetails } = useQuery([QK.gpContacts, clientID], () =>
    getGPDetails(clientID),
    { enabled: !!clientID }
  );

  const { NhsNumber } = useClientDetails(clientID);

  const latestWeight = weights?.[0];

  const deleteMutation = useMutation((id) => deleteMedication(id), {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.clientMedications]);
      enqueueSnackbar("Medication deleted successfully", {
        variant: "success",
      });
      setShowConfirmModal(false);
    },
    onError: () => {
      enqueueSnackbar("Failed to delete medication", { variant: "error" });
    },
  });

  const handleShowUpdate = (id) => {
    setMedId(id);
    setShowUpdateMedicationModal(true);
  };
  const handleShowDetails = (id) => {
    setMedId(id);
    setShowMedicationDetails(true);
  };
  const handleShowDelete = (id) => {
    setMedId(id);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = () => {
    if (medId) deleteMutation.mutate(medId);
  };

  const handleRefillClick = (med) => {
    setSelectedMedObject(med);
    setShowRefillModal(true);
  };

  const handleAdjustClick = (med) => {
    setSelectedAdjustMed(med);
    setShowAdjustModal(true);
  };

  const totalPages = Math.ceil((clientMedications?.count || 0) / 10);

  return (
    <Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2 }}>
        <InfoCard
          icon={<LocalHospital sx={{ fontSize: 20 }} />}
          iconBg="#0284c7"
          bg="#f0f9ff"
          label="NHS NUMBER"
          value={NhsNumber || "N/A"}
        />

        <InfoCard
          icon={<MonitorWeight sx={{ fontSize: 20 }} />}
          iconBg="#16a34a"
          bg="#f0fdf4"
          label="LATEST WEIGHT"
          value={`${latestWeight?.weight || "N/A"} kg`}
          extra={
            latestWeight?.date_time && (
              <Typography
                sx={{ fontSize: "0.65rem", color: "#64748b", fontWeight: 500 }}
              >
                • Recorded: {formatDateTime(latestWeight.date_time)}
              </Typography>
            )
          }
        />

        <InfoCard
          icon={<LocalHospital sx={{ fontSize: 20 }} />}
          iconBg="#475569"
          bg="#f8fafc"
          label="REGISTERED GP"
          value={
            gpDetails
              ?.map((gp) => `Dr. ${gp.first_name} ${gp.last_name}`)
              .join(", ") || "No GP Assigned"
          }
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Button
          fullWidth
          variant="contained"
          color={allergies?.length > 0 ? "error" : "inherit"}
          onClick={() => setShowAllergies(!showAllergies)}
          startIcon={
            showAllergies ? (
              <Close sx={{ fontSize: 18 }} />
            ) : (
              <WarningAmber sx={{ fontSize: 18 }} />
            )
          }
          endIcon={
            showAllergies ? (
              <ExpandLess sx={{ fontSize: 20 }} />
            ) : (
              <ExpandMore sx={{ fontSize: 20 }} />
            )
          }
          sx={{
            borderRadius: "8px",
            py: 1,
            fontWeight: 600,
            fontSize: "0.75rem",
            textTransform: "none",
            boxShadow: "none",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {showAllergies
              ? "Hide Clinical Alerts"
              : `Clinical Allergy Alerts (${allergies?.length || 0})`}
            {!showAllergies && allergies?.length > 0 && (
              <Typography
                sx={{
                  fontSize: "0.68rem",
                  fontWeight: 500,
                  opacity: 0.9,
                  fontStyle: "italic",
                }}
              >
                • Click to view
              </Typography>
            )}
          </Box>
        </Button>

        <Collapse in={showAllergies}>
          <Paper
            variant="outlined"
            sx={{
              mt: 1,
              borderRadius: "10px",
              borderColor: "#fca5a5",
              bgcolor: "#fffcfc",
              overflow: "hidden",
            }}
          >
            <Table size="small">
              <TableHead sx={{ bgcolor: "#fee2e2" }}>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: "#991b1b",
                      fontSize: "0.7rem",
                    }}
                  >
                    Substance
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: "#991b1b",
                      fontSize: "0.7rem",
                    }}
                  >
                    Comment
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allergies?.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
                      {a.name}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem" }}>
                      {a.comment}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Collapse>
      </Box>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.5 }}
      >
        <Typography
          sx={{
            fontSize: "0.85rem",
            fontWeight: 700,
            color: "#1e293b",
            letterSpacing: 0.2,
          }}
        >
          MEDICATION PROTOCOLS
        </Typography>
        <Stack direction="row" spacing={0.75}>
          <Button
            startIcon={<AddCircleOutline sx={{ fontSize: 16 }} />}
            variant="contained"
            size="small"
            onClick={() => setShowMedicationModal(true)}
            sx={{
              borderRadius: "8px",
              bgcolor: "#2469F0",
              fontSize: "0.72rem",
              fontWeight: 600,
              textTransform: "none",
              py: 0.7,
              px: 2,
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#1A5BD4",
                boxShadow: "0 4px 12px rgba(36, 105, 240, 0.25)",
              },
            }}
          >
            Add Entry
          </Button>
          <IconButton
            size="small"
            onClick={() => window.print()}
            sx={{
              border: "1px solid #cbd5e1",
              borderRadius: "8px",
              p: 0.7,
              color: "#475569",
              "&:hover": {
                bgcolor: "#f1f5f9",
                borderColor: "#94a3b8",
              },
            }}
          >
            <Print sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>
      </Stack>

      <MedicationsTable
        medications={clientMedications?.results}
        isLoading={isLoading}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        onRefill={handleRefillClick}
        onAdjust={handleAdjustClick}
        onView={handleShowDetails}
        onEdit={handleShowUpdate}
        onDelete={handleShowDelete}
        isAdmin={profile?.user_type === "admin"}
      />

      <AddMedicationModal
        open={showMedicationModal}
        handleClose={() => setShowMedicationModal(false)}
      />
      <UpdateMedicationModal
        open={showUpdateMedicationModal}
        handleClose={() => setShowUpdateMedicationModal(false)}
        MedicineId={medId}
      />
      <MedicationDetailsModal
        open={showMedicationDetails}
        handleClose={() => setShowMedicationDetails(false)}
        medId={medId}
      />
      <ConfirmModal
        show={showConfirmModal}
        handleClose={() => setShowConfirmModal(false)}
        handleConfirm={handleConfirmDelete}
        isDelete
        title="Delete Medication?"
        body="This action cannot be undone."
        confirmText="Delete"
        isLoading={deleteMutation.isLoading}
      />
      <RefillMedicationModal
        open={showRefillModal}
        handleClose={() => setShowRefillModal(false)}
        medication={selectedMedObject}
        clientID={clientID}
      />
      <AdjustStockModal
        open={showAdjustModal}
        handleClose={() => {
          setShowAdjustModal(false);
          setSelectedAdjustMed(null);
        }}
        medication={selectedAdjustMed}
        clientID={clientID}
      />
    </Box>
  );
};

export default MedsList;